import { Component, Output, OnInit, EventEmitter, HostBinding, Input, OnDestroy, ElementRef } from '@angular/core';
import * as Highcharts from 'highcharts';
import { Utils } from '../../../../../common/js/Utils';
import { ProductQuoteResponse } from '../../../../js/interfaces/CoverpathTypes';
export interface AgeRange {
  from: number;
  to: number;
}

export interface AgeScrubberPosition {
  x: number;
  y: number;
  rx: number;
  ry: number;
  chartContainer;
  delta: number;
}

@Component({
  selector: 'vst-chart',
  moduleId: module.id,
  templateUrl: './vst-chart.component.html',
})
export class VstChartComponent implements OnInit, OnDestroy {
  // Whenever we use the chart, add this class to help us make the component/chart responsive.
  @HostBinding('class') class: string = 'visual-sales-tool__chart-component';
  @Output() ageChange = new EventEmitter<number>();
  @Output() ageScrubberChange = new EventEmitter<AgeScrubberPosition>();
  @Input() premiumAgeRange: AgeRange = undefined;
  @Input() incomeAgeRange: AgeRange = undefined;
  @Input() conceptIllustration: boolean = false;
  @Input() totalIncome: string;
  @Input() totalPremium: string;
  @Input() premium: ProductQuoteResponse;
  @Input() shouldShowDisbursements: boolean;
  @Input() initialAge: number;
  private originalHCRender = undefined;
  private originalHCMarkPath = undefined;
  private originalWindowSize: number;
  private windowSizeDelta: number;
  static tickPositions: AgeScrubberPosition[];
  public currentAge: number = 100;
  public series;
  public vstChart;
  private plotLineLabel: string =
    '<div class="visual-sales-tool__tooltip"><p class="visual-sales-tool__tooltip-label">Age</p><p class="visual-sales-tool__tooltip-value">{{x}}</p></div>';
  private plotLine = {
    color: '#c0c0c0',
    width: 2,
    value: this.currentAge,
    label: {
      text: this.plotLineLabel.replace(/{{x}}/g, this.currentAge.toString()),
      useHTML: true,
      rotation: 0,
      textAlign: 'center',
    },
  };

  private seriesTemplate = {
    showInLegend: false,
    pointInterval: 1,
    lineWidth: 4,
    marker: {
      fillColor: 'transparent',
      symbol: 'circle',
      radius: 5,
      lineWidth: 1,
    },
    states: {
      hover: {
        halo: false,
      },
    },
  };

  constructor(private elem: ElementRef) {}
  ngOnInit(): void {
    if (this.initialAge !== undefined) {
      this.currentAge = this.initialAge;
      // reset this based on new value of currentAge
      this.plotLine = {
        color: '#c0c0c0',
        width: 2,
        value: this.currentAge,
        label: {
          text: this.plotLineLabel.replace(/{{x}}/g, this.currentAge.toString()),
          useHTML: true,
          rotation: 0,
          textAlign: 'center',
        },
      };
    }
    this.originalHCRender = Highcharts.Tick.prototype.render;
    this.originalHCMarkPath = Highcharts.Tick.prototype.getMarkPath;
    Highcharts.setOptions({
      lang: {
        thousandsSep: ',',
      },
    });
  }

  ngOnDestroy() {
    // remove custom code and just call default handlers from highcharts
    if (this.originalHCMarkPath) {
      Highcharts.Tick.prototype.getMarkPath = this.originalHCMarkPath;
    }
    if (this.originalHCRender) {
      Highcharts.Tick.prototype.render = this.originalHCRender;
    }
  }

  public buildSeries(data, minAge: number): void {
    this.series = {};
    Object.keys(data).forEach((seriesItem) => {
      const seriesData = Object.assign({}, this.seriesTemplate, { pointStart: minAge }, data[seriesItem]);
      delete seriesData.tooltip;
      seriesData.marker.enabled = false;
      this.series[seriesItem] = seriesData;
    });
  }

  private seriesToChart() {
    return Object.keys(this.series).map((seriesItem) => {
      return this.series[seriesItem];
    });
  }

  // draw the tops of the color bands for premium and income ranges
  public drawBandLabels() {
    if (!this.vstChart || !this.conceptIllustration) {
      return;
    }

    if (this.premiumAgeRange) {
      const blueWidth = this.vstChart.xAxis[0].plotLinesAndBands[2].svgElem.getBBox().width;

      // Blue Premium topper
      // Note: Reason why inline style is used instead of class for div is the need for changing the width
      //       angular interplation won't work because this is an injected HTML after the fact
      this.vstChart.xAxis[0].plotLinesAndBands[2].label.attr({
        text:
          "<div style='z-index: -50; font-weight: bold; color: #00214b; background: #62d9fb; width: " +
          blueWidth +
          "px; position: relative; padding: 0px; top: -60px; line-height: 35px; border-radius: 2px 2px 0px 0px; text-align: center;'><span class='vst-label-header ' >Premiums</span></div>",
      });

      //invisible area with middle label
      if (this.premium) {
        this.vstChart.xAxis[0].plotLinesAndBands[3].label.attr({
          text:
            "<div style='z-index: 50; font-weight: bold; width: " +
            blueWidth +
            "px;  position: relative; padding: 0px; top: 60px; line-height: 35px; border-radius: 2px 2px 0px 0px; text-align: left; margin-left: 52px;'><span class='vst-label-line-1 vst-label-white'>Cumulative Annual Premium</span> <br> <span class='vst-label-line-2 vst-label-white'>" +
            Utils.formatCurrency(this.totalPremium, 0) +
            "</span> <br> <span class='vst-label-line-3 vst-label-white'>" +
            Utils.formatCurrency(this.premium.base + this.premium.waiver, 0) +
            ' net annual premium</span></div>',
        });
      }
    }

    if (this.incomeAgeRange && this.incomeAgeRange.to !== 0 && this.shouldShowDisbursements) {
      const greenWidth = this.vstChart.xAxis[0].plotLinesAndBands[4].svgElem.getBBox().width;

      // green income topper
      // Note: Reason why inline style is used instead of class for div is the need for changing the width
      //       angular interplation won't work because this is an injected HTML after the fact
      this.vstChart.xAxis[0].plotLinesAndBands[4].label.attr({
        text:
          "<div style='z-index: -50; font-weight: bold; color: #00214b; background: #5ad13e; width: " +
          greenWidth +
          "px; position: relative; padding: 0px; top: -60px; line-height: 35px; border-radius: 2px 2px 0px 0px; text-align: center;'><span class='vst-label-header'>Income</span></div>",
      });
      //invisible area with middle label
      this.vstChart.xAxis[0].plotLinesAndBands[5].label.attr({
        text:
          "<div style='z-index: 50; font-weight: bold;  width: " +
          greenWidth +
          "px;  position: relative; padding: 0px; top: 60px; line-height: 35px; border-radius: 2px 2px 0px 0px; text-align: left; margin-left: 52px;'><span class='vst-label-line-1 vst-label-green'>Cumulative Retirement Income</span> <br> <span class='vst-label-line-2 vst-label-green'>" +
          Utils.formatCurrency(this.totalIncome, 0) +
          "</span> <br><span class='vst-label-line-3 vst-label-green'> " +
          this.displayTotalBrokenDownByPaymentFrequency(
            this.incomeAgeRange.from,
            this.incomeAgeRange.to,
            parseInt(this.totalIncome),
            true
          ) +
          '/yr</span></div>',
      });
    }
  }

  public renderChart(vstData): void {
    const vstScope = this;
    const chartDefinition: any = {
      title: {
        text: undefined,
      },
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        lineWidth: 5,
        minorGridLineColor: 'transparent',
        events: {
          click: function (e) {
            vstScope.updateLayers(Math.round(e.xAxis[0].value));
          },
          redraw: undefined,
          load: undefined,
        },
      },
      xAxis: {
        plotLines: [this.plotLine],
        title: {
          text: "Insured's Age",
          style: {
            color: '#C1CDE3',
            font: `400 14px "MMBrandonText"`,
          },
        },
        tickInterval: 10,
        tickColor: '#C1CDE3',
        minorGridLineWidth: 0,
        minorTickInterval: 5,
        minorTickColor: '#2C5286',
        minorTickLength: 4,
        minorTickWidth: 2,
        crosshair: {
          enabled: false, // true
          className: 'visual-sales-tool__crosshair',
          color: 'rgba(255,255,255,0.3)',
          width: 2,
        },
        max: 100,
        tickWidth: 2,
        tickLength: 8,
        endOnTick: true,
        lineColor: 'rgba(193,205,227, 0.5)',
        lineWidth: 2,
        tickmarkPlacement: 'between',
        labels: {
          style: {
            color: '#C1CDE3',
            font: `400 14px "MMBrandonText"`,
          },
          y: 30,
        },
      },
      yAxis: {
        title: undefined,
        gridLineColor: 'transparent',
        lineColor: 'rgba(193,205,227, 0.5)',
        lineWidth: 2,
        tickLength: 8,
        tickWidth: 2,
        tickColor: '#99abc4',
        minorGridLineWidth: 0,
        minorTickColor: '#99abc4',
        minorTickLength: 8,
        minorTickWidth: 2,
        labels: {
          style: {
            color: '#99abc4',
            font: `400 14px "MMBrandonText"`,
          },
          formatter: function () {
            if (this.value === 0) {
              return '$0';
            } else if (this.value >= 1000000) {
              return '$' + this.value / 1000000 + 'M';
            } else {
              return '$' + this.value / 1000 + 'k';
            }
          },
        },
      },
      tooltip: {
        enabled: true,
        positioner: function (width, height, point) {
          return {
            x: point.plotX + this.chart.hoverSeries.xAxis.left - width / 2,
            y: 0,
          };
        },
        style: {
          opacity: '0.5',
          font: `400 14px "MMBrandonText"`,
        },
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        formatter: function () {
          return (
            '<div class="visual-sales-tool__tooltip visual-sales-tool__tooltip--hover">' +
            '<p class="visual-sales-tool__tooltip-label">Age</p>' +
            '<p class="visual-sales-tool__tooltip-value">' +
            this.x +
            '</p>' +
            '</div>'
          );
        },
        useHTML: true,
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: function () {
                vstScope.updateLayers(this.x);
              },
            },
          },
          animation: {
            duration: 500,
          },
        },
      },
      series: this.seriesToChart(),
    };

    // if these are set, then the chart is being used for concept illustrations, so modify the chart accordingly
    if (this.conceptIllustration && (this.premiumAgeRange || this.incomeAgeRange)) {
      this.updateYaxisMax(vstData, chartDefinition);
      this.updateChartForCI(chartDefinition);
    }

    this.vstChart = new Highcharts.chart('container', chartDefinition);

    this.movePlotBandsUp();

    if (this.conceptIllustration) {
      // prepare for tracking window size changes
      this.originalWindowSize = $(window).width();
      this.windowSizeDelta = 0;

      // when the window resizes we have to make sure to recaculate the X and Y coordinates to match the new
      // position of the window since they're no longer the same
      $(window).resize((e) => {
        this.windowSizeDelta = $(window).width() - this.originalWindowSize;
        // use the bounding rectangle of the chart to recaculate X and Y using the relative X and Y values
        // that are stored in rx, ry
        const chartRect = this.vstChart.container.getBoundingClientRect();
        VstChartComponent.tickPositions = VstChartComponent.tickPositions.map((obj) => {
          obj.x = obj.rx + chartRect.x;
          obj.y = obj.ry + chartRect.y;
          obj.delta = this.windowSizeDelta;
          return obj;
        });
      });
    }
  }

  public movePlotBandsUp() {
    // get plot bands
    const elements = this.elem.nativeElement.querySelectorAll('.highcharts-plot-band');

    elements.forEach((element) => {
      element.setAttribute('transform', ' scale(1, 1.04) translate(0,-22.5)');
    });
  }

  private updateChartForCI(chartDefinition) {
    VstChartComponent.tickPositions = new Array();

    // update main chart items
    chartDefinition.chart.width = 1100;
    chartDefinition.chart.height = 550;
    chartDefinition.legend = { enabled: true };
    chartDefinition.tooltip.enabled = true;
    chartDefinition.tooltip.positioner = function (width, height, point) {
      return {
        x: point.plotX + this.chart.hoverSeries.xAxis.left - width / 2,
        y: height / 2,
      };
    };

    chartDefinition.tooltip.formatter = function () {
      return '<div class="visual-sales-tool__agetooltip">' + this.x + '</div>';
    };

    // update the xAxis
    chartDefinition.xAxis.opposite = true;
    chartDefinition.xAxis.max = 100;
    chartDefinition.xAxis.plotBands = [];
    chartDefinition.xAxis.lineWidth = 0;
    chartDefinition.xAxis.minorTickInterval = 1;
    chartDefinition.xAxis.tickInterval = 5;
    chartDefinition.xAxis.title.text = '';
    chartDefinition.xAxis.labels.y = 3;
    chartDefinition.xAxis.legend = { enabled: false };

    // create the age plotline and a plot line overlay. The overlay is here
    // because otherwise it's too difficult to select the plotline itself. The plot
    // line has dashes and can only be selected on the visible parts. It's also very
    // narrow. The overlay is mostly transparent, and is also wider.
    chartDefinition.xAxis.plotLines = [
      {
        className: 'visual-sales-tool__plotline',
        value: this.currentAge,
        zIndex: 10,
        label: {
          text: '<div class="visual-sales-tool__agescrubber">' + this.currentAge + '</div>',
          useHTML: true,
          rotation: 0,
        },
      },
      {
        className: 'visual-sales-tool__plotlineoverlay',
        value: this.currentAge,
        zIndex: 11,
      },
    ];

    // update the y axis
    chartDefinition.yAxis.tooltip = { enabled: false };
    chartDefinition.yAxis.legend = { enabled: false };
    chartDefinition.yAxis.min = 0;
    chartDefinition.yAxis.plotLines = [
      {
        color: 'rgba(193,205,227, 0.5)',
        width: 2,
        value: 5.5,
      },
    ];

    // setup the premium age plotband
    if (this.premiumAgeRange) {
      chartDefinition.xAxis.plotBands.push({
        label: {
          text: '',
          useHTML: true,
        },
        from: this.premiumAgeRange.from,
        to: this.premiumAgeRange.to,
        color: 'rgba(26,201,230,0.1)',
        zIndex: 0,
      });

      chartDefinition.xAxis.plotBands.push({
        label: {
          text: '',
          useHTML: true,
        },
        from: this.premiumAgeRange.from,
        to: this.premiumAgeRange.to,
        color: '#ffffff00',
        zIndex: 1,
      });
    }

    // setup the income age plotband - this might be non-existent or 0 because
    // disbursment isn't set and therefore there is no income range
    if (this.incomeAgeRange && this.incomeAgeRange.to !== 0 && this.shouldShowDisbursements) {
      chartDefinition.xAxis.plotBands.push({
        label: {
          text: '',
          useHTML: true,
        },
        from: this.incomeAgeRange.from,
        to: this.incomeAgeRange.to,
        color: 'rgba(90,209,63,0.1)',
        zIndex: 0,
      });

      chartDefinition.xAxis.plotBands.push({
        label: {
          text: '',
          useHTML: true,
        },
        from: this.incomeAgeRange.from,
        to: this.incomeAgeRange.to,
        color: '#ffffff00',
        zIndex: 1,
      });
    }

    // Add the event handlers for drawing the band labels
    // above the chart
    chartDefinition.chart.events.redraw = () => {
      this.drawBandLabels();
    };
    chartDefinition.chart.events.render = () => {
      // check if this.vstChart is valid because sometimes we have this event called before the member is set
      // properly. This can be fixed by simply calling the method after a short timeout
      if (!this.vstChart) {
        setTimeout(() => {
          this.drawBandLabels();
          this.ageScrubberChange.emit(VstChartComponent.tickPositions[this.currentAge]);
        }, 250);
      } else {
        this.drawBandLabels();
        this.ageScrubberChange.emit(VstChartComponent.tickPositions[this.currentAge]);
      }
    };

    // handle when a user clicks on the chart to update the age scrubber and details
    chartDefinition.chart.events.click = (e) => {
      this.currentAge = Math.round(e.xAxis[0].value);
      this.updateAgeLabel();

      // set the value of the plot line because otherwise on redraw it ends up in the wrong
      // location on the chart
      this.vstChart.xAxis[0].plotLinesAndBands[0].options.value = this.currentAge;
      this.vstChart.xAxis[0].plotLinesAndBands[1].options.value = this.currentAge;

      this.ageChange.emit(this.currentAge);
      //this.ageScrubberChange.emit(VstChartComponent.tickPositions[this.currentAge]);

      this.vstChart.xAxis[0].update();
      this.movePlotBandsUp();
    };

    // install a custom handler for rendering the marks as circles
    Highcharts.wrap(
      Highcharts.Tick.prototype,
      'getMarkPath',
      function (prev, x, y, tickLength, tickWidth, horiz, renderer) {
        if (horiz) {
          const rect = this.axis.chart.container.getBoundingClientRect();
          let newDelta = 0;
          if (VstChartComponent.tickPositions[this.pos]) {
            newDelta = VstChartComponent.tickPositions[this.pos].delta
              ? VstChartComponent.tickPositions[this.pos].delta
              : 0;
          }
          VstChartComponent.tickPositions[this.pos] = {
            x: rect.x + x,
            y: rect.y + y,
            rx: x,
            ry: y,
            chartContainer: this.axis.chart.container,
            // if there's an original delta here, preserve it when redrawing
            delta: newDelta,
          };
        }
        return this.pos % 5 === 0 ? undefined : renderer.arc(x, y, 1, 0, -Math.PI / 2, (3 * Math.PI) / 2).d.split(' ');
      }
    );

    // install a custom handler for returning the proper color for the mark or the text depending on what it is
    // and if it's in the color band
    Highcharts.wrap(Highcharts.Tick.prototype, 'render', function (prev, index, old, opacity) {
      try {
        prev.apply(this, Array.prototype.slice.call(arguments, 1));
      } catch (e) {}

      if (this) {
        // if this is a valid object check to see if it's a mark or a label to see what we're changing (if anything)
        if (this.mark) {
          this.mark.attr({
            fill: '#C1CDE3',
          });
        }
        if (this.label && this.axis.options.isX) {
          // @TODO this will only work with the last element on the axis and we have to deal with charts that
          //  need more than one element that's three digits - it'll mean the chart needs to be wider to make this work (or
          // something else)
          if (this.pos === 100) {
            this.label.element.innerHTML =
              '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;' + this.label.element.innerHTML;
          }

          // find the plotband the value is within and color it accordingly
          this.axis.options.plotBands.forEach((p) => {
            if (this.pos >= p.from && this.pos <= p.to) {
              if (p.color === 'rgba(26,201,230,0.1)') {
                this.label.css({ color: '#62d9fb' });
              } else if (p.color === 'rgba(90,209,63,0.1)') {
                this.label.css({ color: '#5ad13e' });
              }
              return;
            }
          });
        }
      }
    });
  }

  private updateYaxisMax(vstData, chartDefinition) {
    const ageIndex: number = vstData.highestBenefitAgeIndex ? vstData.highestBenefitAgeIndex : 100 - vstData.ages[0];

    const totalDeath = vstData.netDeathBenefit.data[ageIndex];
    const totalCash = vstData.totalCashValues.data[ageIndex];

    const combinedTotal = vstData.combinedTotalBenefit.data[ageIndex];

    const arrayOfMaxValues = [];

    if (totalDeath) {
      arrayOfMaxValues.push(totalDeath);
    }
    if (totalCash) {
      arrayOfMaxValues.push(totalCash);
    }
    if (combinedTotal) {
      arrayOfMaxValues.push(combinedTotal);
    }

    let yAxisMax = Math.max.apply(Math, arrayOfMaxValues);

    yAxisMax *= 1.55;

    // this will determine how many ticks should be shown on the y axis
    if (yAxisMax < 500000) {
      chartDefinition.yAxis.tickInterval = 50000;
    } else if (yAxisMax < 2000000) {
      chartDefinition.yAxis.tickInterval = 500000;
    } else if (yAxisMax > 2000000 && yAxisMax < 10000000) {
      chartDefinition.yAxis.tickInterval = 1000000;
    } else if (yAxisMax > 10000000) {
      chartDefinition.yAxis.tickInterval = 5000000;
    }

    // determines to yAxis max
    chartDefinition.yAxis.max = yAxisMax; //RealMax > 8500000 ? 8500000 : RealMax;
  }

  private updateAgeLabel() {
    // use left in the styling to move properly - just have to figure out why it's offset incorrectly
    this.vstChart.xAxis[0].plotLinesAndBands[0].options.label.text =
      '<div class="visual-sales-tool__agescrubber">' + this.currentAge + '</div>';
  }

  public updateLayers(x) {
    if (!this.conceptIllustration) {
      this.vstChart.xAxis[0].options.plotLines[0].label.text = this.plotLineLabel.replace(/{{x}}/g, x.toString());
      this.vstChart.xAxis[0].options.plotLines[0].value = x;
      this.movePlotBandsUp();
    }
    this.vstChart.xAxis[0].update();
    this.ageChange.emit(x);
  }

  public displaySeries(display: boolean, series: string): void {
    if (display) {
      this.vstChart.addSeries(this.series[series], true);
    } else {
      const seriesName = this.series[series].name;
      for (let i = 0; i < this.vstChart.series.length; i++) {
        if (this.vstChart.series[i].name === seriesName) {
          this.vstChart.series[i].remove(true);
          break;
        }
      }
    }
    this.vstChart.redraw();
  }

  displayTotalBrokenDownByPaymentFrequency(startAge: number, endAge: number, totalMoney: number, isYear: boolean) {
    const diviser = endAge - startAge;
    const perYear = totalMoney / diviser;

    if (isYear) {
      return Utils.formatCurrency(perYear, 0);
    } else {
      return Utils.formatCurrency(perYear / 12, 0);
    }
  }

  updateAge(age: number) {
    this.updateLayers(age);
  }
}
